import { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  BACKGROUND_OPTIONS,
  PRODUCT_CATEGORY_STATEMENT_COPY as CATEGORY_COPY,
  blockLabel,
  productNameOffered,
  type BlockType,
  type DetailJobView,
} from "@makit/shared";

import { PageHead } from "../app/PageHead";
import { Button, Field, Swatch } from "../components/primitives";
import { LimitBanner, ScopeBanner } from "../components/banners";
import { Toast } from "../components/feedback";
import { ToggleRow } from "../components/settings";
import { PhotoOrder, type PhotoOrderRow } from "../features/review/PhotoOrder";
import { absoluteUrl, generateProductName, reorderJobImages, updateJob, updateJobBlocks } from "../lib/api";
import { previewUrlOf, thumbUrlOf } from "../lib/rendition";
import {
  CANVAS_SCALE_LABEL,
  LOCKED_ITEMS,
  MAX_IMAGE_HEIGHT_PX,
  SPEC,
  blockDimLabel,
} from "../lib/channel-spec";
import type { ShellJob } from "../lib/shell-job";
import { useJobView } from "../lib/use-job-view";
import { rejectionToast } from "../lib/edit-messages";

/** 미리보기 블록의 배지 문구. **크기는 판매처 규격에서 읽는다** — 화면에 숫자를 박으면
 *  판매처가 늘 때 그 문구부터 거짓말이 된다(PRD 7.7.9 ⓑ). */
function badge(block: BlockType, on: boolean, suffix?: string, grownHeight?: number): string {
  // 꺼진 블록은 투명도만으로 표시하지 않는다. 축소해 보면 구분이 안 된다.
  if (!on) return `${block}. 제외됨`;
  return `${block} ${blockDimLabel(block, grownHeight)}${suffix ? ` · ${suffix}` : ""}`;
}

export function ReviewScreen() {
  const job = useOutletContext<ShellJob | undefined>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { view } = useJobView(job?.id);

  const [toast, setToast] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  function announce(message: string) {
    setToast(message);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToast(null), 4000);
  }

  // ── 상품명 ───────────────────────────────────────────────────────────────
  //
  // ⛔ **`job.name` 을 초기값으로 쓰지 않는다.** 그 값은 `toShellJob` 이 `null` 을
  // **`"이름 없는 작업"`** 으로 바꾼 뒤의 것이라, 저장 라우트가 선 지금 그대로 저장하면
  // **그 문구가 진짜 상품명으로 DB 에 박힌다.** 표시용 기본값과 저장할 값은 다른 것이다.
  const serverName = view?.productName ?? null;
  const [name, setName] = useState(serverName ?? "");
  const [draft, setDraft] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const draftRequest = useRef(0);
  useEffect(() => {
    draftRequest.current += 1;
    setDraft(null);
    setGenerating(false);
    setApplying(false);
    setDraftError(null);
    setName(serverName ?? "");
    return () => { draftRequest.current += 1; };
  }, [job?.id, serverName]);
  // 서버 값이 바뀌면(다른 기기에서 고쳤거나 방금 저장했다) 입력칸을 맞춘다. 셀러가 지금
  // 치고 있는 중에는 덮지 않으려고 **서버 값이 바뀔 때만** 따라간다.
  useEffect(() => {
    setName(serverName ?? "");
  }, [serverName]);

  if (!view || !job) {
    return <ReviewLoading />;
  }

  // 가드를 지난 뒤의 작업 id. **닫힌 함수 안에서 `job!` 을 쓰지 않으려고 여기서 잡는다** —
  // 타입 단언은 "지금은 맞다"는 약속이고, 그 약속이 틀리는 날 터지는 자리가 저장 경로다.
  const jobId = job.id;
  const images = [...view.images].sort((a, b) => a.sortOrder - b.sortOrder);
  const enabledOf = (blockType: string): boolean =>
    view.blocks.find((b) => b.blockType === blockType)?.enabled ?? false;
  // **규격에 있는 블록만 그린다.** 셀러가 끈 블록과 채널에 애초에 없는 블록은 다른 것이라,
  // 뭉치면 셀러가 켜 보고 아무 일도 안 일어나는 것을 겪는다(`channelHasBlock` 의 근거).
  const blockTypes = SPEC.blocks.map((b) => b.blockType);
  const onCount = blockTypes.filter(enabledOf).length;

  // ⭐ `A-50b` 의 조건. ⛔ **여기서 `statement === "COSMETIC"` 처럼 직접 비교하지 않는다** —
  // 서버의 409 와 같은 함수에서 나와야 한다(`productNameOffered`). 두 벌이 되면 값이 하나
  // 느는 날 **화면은 열려 있는데 서버가 막는** 상태가 된다.
  // ⚠️ **진술이 없으면(`null`) 막지 않는다.** 그 판단 근거는 그 함수 주석에 있다.
  const nameOffered = productNameOffered(view.productCategoryStatement ?? null);

  /** 저장 응답을 캐시에 바로 꽂는다. **다시 조회하지 않는다** — 서버가 이미 갱신된 작업
   *  전체를 돌려주므로, 재조회하면 같은 것을 한 번 더 받으면서 화면이 잠깐 옛 값으로
   *  깜빡인다. 머리줄(`JobShell`)도 같은 키를 보므로 함께 따라온다. */
  function commit(updated: DetailJobView) {
    queryClient.setQueryData(["job", jobId], updated);
  }

  /** ⛔ **실패를 삼키지 않는다.** 이 화면은 저장 버튼이 없어서, 실패했는데 `저장됨` 이라고
   *  적으면 셀러는 그 말을 믿고 창을 닫는다. 다음에 열면 값이 돌아와 있다. */
  async function save(what: string, run: () => Promise<DetailJobView>) {
    try {
      commit(await run());
      announce(`${what} · 저장됨`);
    } catch (err) {
      // **사유를 그대로 말한다.** 팔레트 밖 배경(`BACKGROUND_NOT_ALLOWED`)이나 갈린
      // 사진 목록(`IMAGE_SET_MISMATCH`)은 다시 눌러도 같은 결과다 — 「잠시 뒤」로 뭉개면
      // 셀러가 같은 실패를 반복한다(`lib/edit-messages.ts`).
      announce(rejectionToast(err, "저장하"));
    }
  }

  const swatchIndex = BACKGROUND_OPTIONS.findIndex((o) => o.hex === view.backgroundColor);
  const currentHex = view.backgroundColor;

  // 사진은 장 번호로 부른다(PRD 7.5.9). 앱 `A-51` 과 같은 식이다. 파일 이름도 배열 위치도 쓰지 않는다.
  const photos: PhotoOrderRow[] = images.map((img) => ({
    id: img.id,
    name: `${img.sortOrder + 1}번 사진`,
    ...(absoluteUrl(thumbUrlOf(img)) ? { src: absoluteUrl(thumbUrlOf(img))! } : {}),
  }));

  const heroSrc = absoluteUrl(images[0] ? previewUrlOf(images[0]) : null);
  const gallery = images.slice(1);

  return (
    <>
      {/* 상품명·썸네일·상태 칩·판 번호는 작업 셸이 그린다. 이 화면이 더하는 것은
          우측 `저장됨 · 방금` 하나뿐이다(PRD 7.7.4). */}
      <PageHead frame="W-30" title="검토 · 고치기" />

      <div className="review" style={{ marginTop: 16 }}>
        <div className="canvas-wrap">
          <div className="canvas-scale">
            <span className="mono">{CANVAS_SCALE_LABEL}</span>
            {/* ⚠️ **세로 합계를 적지 않는다.** `spec`·`notice` 는 규격 높이가 `null`(내용에
                따라 늘어남)이고 지금은 파일이 아예 만들어지지 않아, 합계를 적으면 **셀 수
                없는 것을 센 척**하게 된다. 실제 세로는 판이 생긴 뒤 `W-40` 이 말한다. */}
            <span className="mono">
              켠 블록 {onCount}종 · 사진 {images.length}장
            </span>
          </div>
          <div className="page">
            <div
              className={`block b-hero${enabledOf("hero") ? "" : " off"}`}
              style={{ background: currentHex }}
            >
              <span className="blabel mono">{badge("hero", enabledOf("hero"))}</span>
              {heroSrc ? (
                <img className="blockimg" src={heroSrc} alt="" />
              ) : (
                <span className="hero-obj" />
              )}
              <span className="hero-title">{name || "상품명 없음"}</span>
            </div>

            {gallery.slice(0, 2).map((img, i) => {
              const src = absoluteUrl(previewUrlOf(img));
              return (
                <div
                  key={img.id}
                  className={`block b-gal${enabledOf("gallery") ? "" : " off"}`}
                  style={{ background: currentHex }}
                >
                  <span className="blabel mono">
                    {badge("gallery", enabledOf("gallery"), `${i + 1} / ${gallery.length}`)}
                  </span>
                  {src ? (
                    <img className="blockimg" src={src} alt="" />
                  ) : (
                    <span className={`gal-obj${i % 2 ? " alt" : ""}`} />
                  )}
                </div>
              );
            })}

            {gallery.length > 2 ? (
              <div className="block more">상세컷 {gallery.length - 2}장은 미리보기에서 접었습니다</div>
            ) : null}

            <div
              className={`block b-spec${enabledOf("spec") ? "" : " off"}`}
              style={{ background: currentHex }}
            >
              <span className="blabel mono">{badge("spec", enabledOf("spec"), undefined, 460)}</span>
              {/* ⭐ **서버가 준 항목을 그린다**(`view.specItems`). 예시 행(`SAMPLE_SPEC_ROWS`)을
                  그리던 자리다. 항목을 넣는 길(VLM 추출 · 셀러 입력)이 아직 없어 지금은 대개
                  비어 있고(PRD `7.7.4`), 그때는 행을 안 세운다. ⛔ 예시로 채우지 마라. 서버
                  작업 화면에 남의 상품 값이 서는 것이 고친 이유다.
                  ⚠️ **읽기만 한다.** 고치는 라우트가 없다. 순서는 서버의 `sortOrder` 다. */}
              {[...view.specItems]
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((item) => (
                  <div className="spec-row" key={item.id}>
                    <span>{item.label}</span>
                    <span>{item.value}</span>
                  </div>
                ))}
            </div>

            <div
              className={`block b-notice${enabledOf("notice") ? "" : " off"}`}
              style={{ background: currentHex }}
            >
              <span className="blabel mono">
                {badge("notice", enabledOf("notice"), undefined, 320)}
              </span>
              <div className="spec-row">
                <span>배송·교환 안내</span>
                <span>스토어 설정에서 가져옴</span>
              </div>
            </div>
          </div>
        </div>

        <div className="editpanel">
          <ScopeBanner>
            <b>바꿀 수 있는 것은 넷입니다.</b> 사진 순서 · 블록 포함 여부 · 텍스트 내용 · 배경. 아래
            네 섹션이 그 넷이고, 마지막 섹션이 <b>그 바깥</b>입니다.
          </ScopeBanner>

          <section>
            <h3>① 사진 순서</h3>
            <PhotoOrder
              photos={photos}
              onReorder={(next) =>
                void save("사진 순서", () =>
                  reorderJobImages(
                    jobId,
                    // ⛔ 그 작업의 사진 **전부**를 보낸다. 부분 배열은 서버가 거절한다.
                    next.map((p) => p.id),
                  ),
                )
              }
            />
          </section>

          <section>
            <h3>② 상품명</h3>
            <Field
              value={name}
              aria-label="상품명"
              placeholder="상품명을 입력해 주세요"
              disabled={applying}
              onChange={(e) => {
                draftRequest.current += 1;
                setGenerating(false);
                setDraft(null);
                setName(e.target.value);
              }}
              onBlur={() => {
                const next = name.trim() === "" ? null : name.trim();
                // 바뀐 것이 없으면 부르지 않는다. 칸을 지나가기만 해도 저장 요청이 나가면
                // 이력에 안 바뀐 줄이 쌓인다.
                if (next === serverName) return;
                void save("상품명", () => updateJob(jobId, { productName: next }));
              }}
            />
            <p className="hint">상품명은 최대 2줄로 표시됩니다.</p>
            {/* ⭐ `A-50b`. **셀러가 화장품·건강기능식품이라고 진술한 작업**은 상품명을
                만들어 드리지 않는다(`A-26c` 에서 고르신 값. PRD `7.6.9b`).
                ⛔ **상품명 칸은 위에 살아 있다.** 막힌 것은 **만들어 드리는 일**이지
                **적는 일**이 아니다 — 칸까지 잠그면 셀러가 자기 상품을 팔 수 없게 된다.
                ⛔ **업로드를 막는 것으로 읽히면 안 된다.** 막는 근거가 표시·광고라
                **글자를 만드는 자리 하나**에만 걸린다. */}
            {nameOffered ? (
              <p className="hint">상품명 초안을 만들기 위해 배경을 지운 사진 1장을 Cloudflare AI로 보냅니다.</p>
            ) : (
              <>
                <LimitBanner>
                  <span className="l1">{CATEGORY_COPY.blockedTitle}</span>
                  <span className="l2">{CATEGORY_COPY.blockedBody}</span>
                </LimitBanner>
                {/* ⭐ **창구가 선택이 아니라 필수다.** 고를 값의 목록 자체가 틀릴 수 있고,
                    **틀린 쪽이 우리 목록일 때 셀러에게 출구가 있어야** 한다.
                    ⛔ `이의 제기`·`소명`·`재심사` 로 적지 않는다 — 셋 다 **셀러가
                    심판받는 자리**로 읽히는데 틀린 쪽은 우리 목록이다. */}
                {/* `창구 미정` 배지를 걷었다 (2026-10-04). 알려 주실 곳은 화면 맨 아래 사업자
                    정보의 연락처다. 문장 자체는 출구라 남긴다(`product-category-statement.test`).
                    ⛔ 「고치시는 자리는 결제 화면의 상품 종류 하나입니다」(`가는 길 미정`)는 문장째
                    걷었다. 결제가 끝난 작업에서 그 화면으로 가는 길이 없어, 배지만 떼면 갈 수 없는
                    곳을 갈 수 있다고 말하게 된다. */}
                <p className="hint">{CATEGORY_COPY.blockedAppeal}</p>
              </>
            )}
            <Button label={generating ? "상품명 만드는 중" : "사진에서 상품명 만들기"} disabled={!nameOffered || generating || applying || !!name.trim() || !!serverName} disabledReason={!nameOffered ? CATEGORY_COPY.blockedTitle : undefined} onClick={() => {
              const requestId = ++draftRequest.current;
              setGenerating(true);
              setDraftError(null);
              setDraft(null);
              void generateProductName(jobId).then((result) => {
                if (draftRequest.current === requestId) setDraft(result.productName);
              }).catch(() => {
                if (draftRequest.current === requestId) setDraftError("상품명을 만들지 못했습니다. 직접 입력하거나 다시 시도해 주세요.");
              }).finally(() => {
                if (draftRequest.current === requestId) setGenerating(false);
              });
            }} />
            {(name.trim() || serverName) && <p className="hint">입력한 상품명이 있어 초안을 만들 수 없습니다.</p>}
            {draftError && <p role="status">{draftError}</p>}
            {draft !== null && <div aria-label="상품명 초안">
              <h4>상품명 초안</h4>
              <p className="hint">사진에서 만든 초안입니다. 상품과 맞는지 확인하고 고쳐 주세요.</p>
              <Field aria-label="상품명 초안" value={draft} disabled={applying} onChange={(event) => setDraft(event.target.value)} />
              <Button label="확인하고 적용" disabled={applying || !draft.trim() || !!name.trim() || !!serverName} onClick={() => {
                const requestId = ++draftRequest.current;
                setApplying(true);
                void updateJob(jobId, { productName: draft.trim() }, true).then((updated) => {
                  if (draftRequest.current === requestId) { commit(updated); setDraft(null); announce("상품명 · 저장됨"); }
                }).catch((error) => {
                  if (draftRequest.current === requestId) announce(rejectionToast(error, "저장하"));
                }).finally(() => { if (draftRequest.current === requestId) setApplying(false); });
              }} />
            </div>}
          </section>

          <section>
            <h3>③ 블록</h3>
            {blockTypes.map((key) => (
              <ToggleRow
                key={key}
                label={blockLabel(key)}
                on={enabledOf(key)}
                onChange={(next) =>
                  void save(`${blockLabel(key)} 블록을 ${next ? "켰습니다" : "껐습니다"}`, () =>
                    updateJobBlocks(jobId, [{ blockType: key, enabled: next }]),
                  )
                }
              />
            ))}
            <p className="hint">
              <b>블록마다 파일을 따로 내보냅니다.</b> 한 장으로 합치지 않는 이유는 판매처가 이미지
              한 장의 세로를 {MAX_IMAGE_HEIGHT_PX.toLocaleString()}px로 제한하기 때문입니다.
            </p>
          </section>

          <section>
            <h3>④ 배경</h3>
            <div className="swatches">
              {/* 값이 정해지지 않은 색(hex 없음)은 그리지 않는다. 이유 문장 없는 빈 칸이 되기 때문이다.
                  `W-80` 환경설정과 같은 처리다. 순번(i)은 원래 팔레트 기준으로 지킨다. */}
              {BACKGROUND_OPTIONS.map((option, i) => ({ option, i }))
                .filter(({ option }) => option.hex !== null)
                .map(({ option, i }) => (
                <Swatch
                  key={option.key}
                  hex={option.hex}
                  name={option.label}
                  selected={swatchIndex === i}
                  onSelect={() => {
                    if (!option.hex || option.hex === currentHex) return;
                    void save(`배경을 ${option.label}으로 바꿨습니다`, () =>
                      updateJob(jobId, { backgroundColor: option.hex! }),
                    );
                  }}
                />
              ))}
            </div>
            <p className="hint">
              고르면 미리보기의 <b>모든 블록 배경이 즉시 바뀝니다.</b>
            </p>
          </section>

          <section className="locked">
            <h3>바꿀 수 없는 것</h3>
            <ul>
              {LOCKED_ITEMS.map(([term, why]) => (
                <li key={term}>
                  <b>{term}</b>. {why}
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>

      {toast ? <Toast message={toast} /> : null}

      <LimitBanner>
        <b>바꾼 값은 서버에 남습니다.</b> 사진 순서 · 상품명 · 블록 · 배경 넷이 그렇습니다.{" "}
        {/* **`결과물` 이 아니라 `상세페이지` 다** (용어 표 전수). 바로 뒤 `미리보기까지만
            갑니다` 와 짝이 되어 **승인 전에 보는 것**과 **우리가 만들어 주는 물건**을 한
            문장에서 두 낱말로 보여준다. */}
        <b>다만 아직 상세페이지에는 닿지 않습니다.</b> 판매처 규격으로 배치하는 단계가 없어 바뀐
        값이{" "}
        <b>미리보기까지만</b> 갑니다.
        {/* ⛔ **`스펙표 항목은 아직 서버에서 오지 않아 예시입니다` 를 걷었다.** 항목은 작업
            응답(`specItems`)에 실려 오고 위 미리보기가 그것을 그린다. 예시가 아니게 됐다.
            비어 있을 때 무엇이라 적을지는 정본이 없어 문장을 새로 짓지 않았다. */}
      </LimitBanner>

      {/* ⛔ 「자유롭게 고치는 기능을 넣지 않습니다」 콜아웃과 「이 화면에서 아직 못 합니다」를
          걷었다 (2026-10-04 사용자 지시). 설계자에게 하는 말이었다. 못 만든 장으로 가는 문
          (`W-30` → `W-21`)은 남긴다. 옛 둘째 문장 「돈이 드는 동작이라…」는 2026-09-23 확정
          (못 만든 장은 더 내실 금액이 없다) 뒤로 거짓이라 옮기지 않았다. */}
      <p className="hint" style={{ marginTop: 12 }}>
        <span className="l1">
          <b>못 만든 장 다시 만들기</b>는 <b>올리는 중</b> 화면에서 합니다.
        </span>
        <span className="l2">
          <Button
            label="올리는 중 화면"
            variant="ghost"
            small
            onClick={() => navigate(`/jobs/${jobId}/upload`)}
          />
        </span>
      </p>
    </>
  );
}

/** 작업이 아직 안 왔을 때. 셸이 같은 질의를 받은 뒤에만 이 화면이 서므로 거의 오지 않는다.
 *
 * ⛔ **예시 작업 갈래(`SampleReview`)를 걷었다** (2026-10-04 사용자 지시). 예시 작업이 없어졌다. */
function ReviewLoading() {
  return (
    <>
      <PageHead frame="W-30" title="검토 · 고치기" />
      <p className="hint" role="status" style={{ marginTop: 12 }}>
        작업을 불러오는 중입니다.
      </p>
    </>
  );
}
